//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import FarmSiteModel from '../models/farm-site.model';

/**
 * @service FarmSiteService
 * @description Service for managing farm site data operations.
 * Provides HTTP methods for retrieving farm site details based on various criteria such as
 * business ID, provider ID, technician ID, and more. Also includes geocoding functionality.
 * Extends base Service class with generic type FarmSiteModel.
 *
 * @feature 1. Fetch farm sites by business, provider, or technician
 * @feature 2. Geocoding support via Google Maps API
 * @feature 3. Role-based data retrieval (Farmer vs Employee vs Provider)
 * @feature 4. Cancel ongoing HTTP requests to prevent memory leaks
 *
 * @architecture
 * Extends base Service<FarmSiteModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /tradingcommunity/farmsite/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class FarmSiteService extends Service<FarmSiteModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/farmsite/');
  }

  /**
   * @method getAllFarmSites
   * @async
   * @description Fetch all farm site records from the backend
   *
   * Makes HTTP GET request to retrieve all farm sites with pagination.
   *
   * @returns {Promise<Array<FarmSiteModel>>} Promise resolving to array of FarmSiteModel objects
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getAllFarmSites() {
    return new Promise<Array<FarmSiteModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FarmSiteModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFarmeSitesByProviderIDandFarmId
   * @async
   * @description Fetch farm sites associated with a specific provider and farm business
   *
   * @param {string} businessId - Farm Business ID
   * @param {string} farmProviderId - Feed Provider ID
   * @returns {Promise<Array<FarmSiteModel>>} Promise resolving to array of FarmSiteModel objects
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getFarmeSitesByProviderIDandFarmId(businessId: string, farmProviderId: string) {
    return new Promise<Array<FarmSiteModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FarmSiteModel>>(`${this.baseUrl}${this.serviceUrl}feedProviderID/${farmProviderId}/businessID/${businessId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFarmeSitesByTechnicianIDs
   * @async
   * @description Fetch farm sites assigned to a list of technicians
   *
   * Uses HTTP PUT to send a complex query object in the body.
   *
   * @param {Array<String>} technicianIds - Array of technician IDs
   * @returns {Promise<Array<FarmSiteModel>>} Promise resolving to array of FarmSiteModel objects
   * @side-effect Creates HTTP PUT request and stores subscription in ongoingRequests
   */
  public getFarmeSitesByTechnicianIDs(technicianIds: Array<String>) {
    return new Promise<Array<FarmSiteModel>>((resolve, reject) => {
      const request = this.httpClient.put<Array<FarmSiteModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new&include=businessID`,
      {
        "where": {
          "contract.feedProviderPersonID": technicianIds
        }
      } ,this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFarmeSitesByBAdminId
   * @async
   * @description Fetch farm sites by business admin IDs (feed provider IDs)
   *
   * Uses HTTP PUT to send a complex query object in the body.
   *
   * @param {Array<String>} businessId - Array of Business IDs
   * @returns {Promise<Array<FarmSiteModel>>} Promise resolving to array of FarmSiteModel objects
   * @side-effect Creates HTTP PUT request and stores subscription in ongoingRequests
   */
  public getFarmeSitesByBAdminId(businessId: Array<String>) {
    return new Promise<Array<FarmSiteModel>>((resolve, reject) => {
      const request = this.httpClient.put<Array<FarmSiteModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new&include=businessID`,
        {
          "where": {
            "contract.feedProviderID": businessId
          }
        } ,this.httpOptions).subscribe(response => {
          resolve(response);
        }, (error: HttpErrorResponse) => {
          reject(error);
        });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFarmeSitesByFarmId
   * @async
   * @description Fetch farm sites by business ID with role-based logic
   *
   * Checks `localStorage.loginBusinessCategory`. If 'Farm', fetches all for context.
   * Otherwise, fetches for specific business ID.
   *
   * @param {string} businessId - Business ID
   * @returns {Promise<Array<FarmSiteModel>>} Promise resolving to array of FarmSiteModel objects
   * @side-effect Reads localStorage
   */
  public getFarmeSitesByFarmId(businessId: string) {
    let queryParam = '';
    if (localStorage.loginBusinessCategory == 'Farm') {
      queryParam = `businessID/`;
    } else {
      queryParam = `businessID/${businessId}`;    
    }
    return new Promise<Array<FarmSiteModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FarmSiteModel>>(`${this.baseUrl}${this.serviceUrl}${queryParam}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFarmeSitesByFarmsId
   * @async
   * @description Fetch farm sites by business ID (Alternative method)
   *
   * Similar to `getFarmeSitesByFarmId`, checks `localStorage.loginBusinessCategory`.
   *
   * @param {string} businessId - Business ID
   * @returns {Promise<Array<FarmSiteModel>>} Promise resolving to array of FarmSiteModel objects
   * @side-effect Reads localStorage
   */
  public getFarmeSitesByFarmsId(businessId: string) {
    let queryParam = '';
    if (localStorage.loginBusinessCategory == 'Farm') {
      queryParam = `businessID/`;
    } else {
      queryParam = `businessID/${businessId}`;    
    }
    return new Promise<Array<FarmSiteModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FarmSiteModel>>(`${this.baseUrl}${this.serviceUrl}${queryParam}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFarmeSitesByFarmManagerIdInFarmSpend
   * @async
   * @description Fetch farm sites by business ID and person ID with role check
   *
   * Checks `localStorage.loginPersonType`. If 'EMPLOYEE', filters by person ID.
   * Otherwise, fetches all for business.
   *
   * @param {string} businessId - Business ID
   * @param {string} personId - Person ID (Manager)
   * @returns {Promise<Array<FarmSiteModel>>} Promise resolving to array of FarmSiteModel objects
   * @side-effect Reads localStorage
   */
  public getFarmeSitesByFarmManagerIdInFarmSpend(businessId: string, personId: string) {
    let queryParam = '';
    if (localStorage.loginPersonType == 'EMPLOYEE') {
      queryParam = `businessID/${businessId}/personID/${personId}`;
    } else {
      queryParam = `businessID//personID/`;
    }
    return new Promise<Array<FarmSiteModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FarmSiteModel>>(`${this.baseUrl}/tradingcommunity/persons/farmSites/${queryParam}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFarmeSitesBusinessDetailsByProviderId
   * @async
   * @description Fetch farm sites for a provider business
   *
   * @param {string} farmProviderId - Provider Business ID
   * @returns {Promise<Array<FarmSiteModel>>} Promise resolving to array of FarmSiteModel objects
   */
  public getFarmeSitesBusinessDetailsByProviderId(farmProviderId: string) {
    return new Promise<Array<FarmSiteModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FarmSiteModel>>(`${this.baseUrl}${this.serviceUrl}feedProviderID/${farmProviderId}/technicianID/-1`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFarmeSitesByTechnicianId
   * @async
   * @description Fetch farm sites assigned to a specific technician
   *
   * @param {string} technicianId - Technician Person ID
   * @returns {Promise<Array<FarmSiteModel>>} Promise resolving to array of FarmSiteModel objects
   */
  public getFarmeSitesByTechnicianId(technicianId: string) {
    return new Promise<Array<FarmSiteModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FarmSiteModel>>(`${this.baseUrl}${this.serviceUrl}?contract.feedProviderPersonID=${technicianId}&pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method latLongsData
   * @async
   * @description Fetch geocoding data (lat/long) for an address using Google Maps API
   *
   * @param {string} village - Village name
   * @param {string} [state] - State name
   * @param {string} [mandal] - Mandal name
   * @param {string} [district] - District name
   * @returns {Promise<FarmSiteModel>} Promise resolving to geocoding result
   * @side-effect Calls external Google Maps API
   */
  public latLongsData(village: string,state?: string,mandal?: string,district?: string) {
    return new Promise<FarmSiteModel>((resolve, reject) => {
      const request = this.httpClient.get<FarmSiteModel>(`https://maps.googleapis.com/maps/api/geocode/json?address=${state}+''+${district}+''+${mandal}+''+${village}&key=AIzaSyD4F5CMr3k_9MgrD1wJ9AYe2FYTWCpD6Fc`).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getDealersFarmersConsultantsList
   * @async
   * @description Fetch list of dealers, farmers, or consultants for a provider
   *
   * @param {string} providerBusinessID - Provider Business ID
   * @param {string} personRole - Role to filter by
   * @param {string} fromDate - Start date filter
   * @param {string} toDate - End date filter
   * @returns {Promise<Array<FarmSiteModel>>} Promise resolving to array of FarmSiteModel objects
   */
  public getDealersFarmersConsultantsList(providerBusinessID: string, personRole: string, fromDate: string, toDate: string) {
    return new Promise<Array<FarmSiteModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FarmSiteModel>>(`${this.baseUrl}${this.serviceUrl}providerAffiliatePersons?providerBusinessID=true&lookoutRole=${personRole}&fromDate=${fromDate}&toDate=${toDate}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
